import { useState, useRef, useEffect } from 'react';
import { ArrowLeft, AlertTriangle, CheckCircle, Upload, Play, Loader2, Cuboid } from 'lucide-react';
import { Scene, ImageFile, ValidationResult } from '../types';
import { UploadResult } from '../types/upload.types';
import { ScanStatus } from '../types/scan.types';
import ImageUploader from '../components/ImageUpload';
import ImageDetails from '../components/ImageDetails';
import { ScanUpload, type ScanUploadRef } from '../components/ScanUpload';
import { DetailedValidationSidebar } from './DetailedValidationSidebar';
import { useScanLifecycle } from '../hooks/useScanLifecycle';
import { useUploadCtx } from '../contexts/UploadContext';


interface Props {
  scene: Scene;
  onBack: () => void;
  onReconstruct: (scene: Scene, sequences: Array<Array<{
    image_key: string;
    timestamp: string;
    image_width_px: number;
    image_height_px: number;
    focal_length_35mm?: number;
  }>>, uploadResult?: UploadResult) => void;
  [key: string]: any; // allow additional props from parent without strict typing
}

type WorkflowStep = 'upload' | 'validating' | 'validation-complete' | 'upload-to-s3' | 'ready-to-reconstruct';

export default function SceneEditor({ scene, onBack, onReconstruct }: Props) {
  const [images, setImages] = useState<ImageFile[]>([]);
  const [selectedImage, setSelectedImage] = useState<ImageFile | null>(null);
  const [canReconstruct, setCanReconstruct] = useState(false);
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([]);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [currentStep, setCurrentStep] = useState<WorkflowStep>('upload');
  const [error, setError] = useState<string | null>(null);
  
  // Scan tracking
  const [createdScanId, setCreatedScanId] = useState<string | null>(null);
  
  // Validation progress tracking
  const [validationProgress, setValidationProgress] = useState({
    step: '',
    progress: 0,
    isValidating: false
  });
  
  // Scan creation progress tracking
  const [scanCreationProgress, setScanCreationProgress] = useState({
    step: '',
    isCreating: false
  });
  
  // Scan lifecycle management
  const { 
    createCompleteWebScan, 
    transitionToUploaded, 
    transitionToProcessing, 
    state: scanState 
  } = useScanLifecycle();
  
  const scanUploadRef = useRef<ScanUploadRef>(null);

  const { sequenceUuid } = useUploadCtx();

  // Update workflow step based on state
  useEffect(() => {
    if (images.length === 0) {
      setCurrentStep('upload');
    } else if (validationProgress.isValidating) {
      setCurrentStep('validating');
    } else if (!canReconstruct && validationResults.length > 0) {
      setCurrentStep('validation-complete');
    } else if (canReconstruct && !uploadComplete) {
      setCurrentStep('upload-to-s3');
    } else if (uploadComplete) {
      setCurrentStep('ready-to-reconstruct');
    }
  }, [images.length, canReconstruct, uploadComplete, validationProgress.isValidating, validationResults.length]);

  const handleImageClick = (image: ImageFile) => {
    setSelectedImage(image);
  };

  const handleBackFromDetails = () => {
    setSelectedImage(null);
  };

  const handleDeleteImage = (id: string) => {
    const updatedImages = images.filter(img => img.id !== id);
    setImages(updatedImages);
    setSelectedImage(null);
    
    if (uploadComplete) {
      setUploadComplete(false);
    }
  };

  const handleValidationStart = () => {
    setValidationProgress({ step: 'Starting validation...', progress: 0, isValidating: true });
    setError(null);
    
    // Create scan with tempsaved status when validation starts
    if (!createdScanId) {
      createScanWithTempSavedStatus();
    }
  };

  const createScanWithTempSavedStatus = async () => {
    try {
      setScanCreationProgress({ step: 'Creating scan record...', isCreating: true });
      console.log('📝 Creating scan with tempsaved status...');
      
      const scanTitle = scene.name || 'Untitled Scan';
      const additionalMetadata = {
        sceneId: scene.id,
        validationStarted: new Date().toISOString(),
        imageCount: images.length
      };

      const { scan, metadata } = await createCompleteWebScan(scanTitle, scene, sequenceUuid ?? '', additionalMetadata);
      console.log('✅ Scan created with tempsaved status:', scan.scanId);
      
      setCreatedScanId(scan.scanId);
      setScanCreationProgress({ step: 'Scan created', isCreating: false });
    } catch (error) {
      console.error('❌ Failed to create scan:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create scan';
      setError(`Scan creation failed: ${errorMessage}`);
      setScanCreationProgress({ step: '', isCreating: false });
    }
  };

  const handleValidationProgress = (progress: number, step: string) => {
    setValidationProgress({ step, progress, isValidating: true });
  };

  const handleValidationChange = (canRecon: boolean, results?: ValidationResult[]) => {
    console.log('🔄 Validation complete:', { canRecon, resultsCount: results?.length });
    setCanReconstruct(canRecon);
    setValidationResults(results || []);
    setValidationProgress({ step: 'Validation complete', progress: 100, isValidating: false });
    setError(null);
    
    if (!canRecon && uploadComplete) {
      setUploadComplete(false);
    }
  };

  const handleUploadComplete = async (result: UploadResult) => {
    console.log('✅ Upload completed successfully:', result);
    setUploadResult(result);
    
    if (!createdScanId) {
      setError('No scan found to update. Please try again.');
      return;
    }
    
    try {
      setScanCreationProgress({ step: 'Updating scan to uploaded status...', isCreating: true });

      // Transition to uploaded status
      console.log('🔄 Transitioning scan to uploaded status...');
      const s3FolderPath = `${result.fullPath}/`; // Use complete S3 path with userId prefix
      const updatedScan = await transitionToUploaded(createdScanId, s3FolderPath, result.fileCount, result.sessionUUID);
      
      console.log('✅ Scan transitioned to uploaded successfully:', updatedScan.scanId);
      console.log('📊 Scan status:', updatedScan.status);
      
      setScanCreationProgress({ step: 'Ready for reconstruction', isCreating: false });
      setUploadComplete(true);
      setError(null);
    } catch (error) {
      console.error('❌ Failed to transition scan to uploaded:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update scan status';
      setError(`Scan update failed: ${errorMessage}`);
      setScanCreationProgress({ step: '', isCreating: false });
      setUploadComplete(false);
    }
  };

  const handleUploadError = (error: string) => {
    console.error('❌ Upload failed:', error);
    setUploadComplete(false);
    setUploadResult(null);
    setError(`Upload failed: ${error}`);
  };

  const handleUploadProgress = (progress: any) => {
    // Don't call setState here to avoid the warning
    console.log('📊 Upload progress:', progress.overallProgress + '%');
  };

  const handleStartUpload = () => {
    if (scanUploadRef.current && canReconstruct) {
      setError(null);
      scanUploadRef.current.startUpload(images);
    }
  };

  const handleUploadNewDataset = () => {
    setImages([]);
    setCanReconstruct(false);
    setValidationResults([]);
    setUploadComplete(false);
    setUploadResult(null);
    setCreatedScanId(null); // Reset scan ID
    setError(null);
    setValidationProgress({ step: '', progress: 0, isValidating: false });
    setScanCreationProgress({ step: '', isCreating: false });
    setCurrentStep('upload');
  };

  const handleStartReconstruction = async () => {
    if (uploadComplete && canReconstruct && uploadResult && createdScanId) {
      try {
        setScanCreationProgress({ step: 'Triggering 3D reconstruction...', isCreating: true });
        console.log('🚀 Calling reconstruction trigger...');
        
        //  Read sessionMetadata.json from S3 first
        console.log('📖 Reading session metadata from S3...');
        const folderPath = uploadResult.fullPath.replace(/\/$/, '');
        
        const { s3MetadataReader } = await import('../services/s3MetadataReader.service');
        const parsed = await s3MetadataReader.readAndValidateForReconstruction(folderPath);
        
        console.log('Session metadata parsed:', {
          sessionUUID: parsed.sessionUUID,
          sequenceCount: parsed.sequences.length,
          totalImages: parsed.sequences.reduce((sum, seq) => sum + seq.length, 0)
        });
        
        // Call reconstruction with sequences from metadata
        await onReconstruct(scene, parsed.sequences, uploadResult);
        
        // Only update scan status to processing AFTER reconstruction succeeds
        console.log('🔄 Transitioning scan to processing status...');
        const processingScene = await transitionToProcessing(createdScanId);
        console.log('✅ Scan transitioned to processing:', processingScene.scanId);
        console.log('📊 Scan status:', processingScene.status);
        
        setScanCreationProgress({ step: 'Reconstruction triggered successfully!', isCreating: false });
        
        // Wait briefly to show success message then refresh the page
        setTimeout(() => {
          console.log('🏠 Refreshing page to reflect processing status...');
          onBack();
        }, 1500);
        
      } catch (error) {
        console.error('❌ Failed to start reconstruction:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to start reconstruction';
        setError(`Reconstruction failed: ${errorMessage}`);
        setScanCreationProgress({ step: '', isCreating: false });
      }
    }
  };

  // Show image details if selected
  if (selectedImage) {
    return (
      <ImageDetails
        image={selectedImage}
        onBack={handleBackFromDetails}
        onDelete={handleDeleteImage}
      />
    );
  }

  // Get validation summary for sidebar
  const getValidationSummary = () => {
    const blockers = validationResults.filter(r => r.category === 'blocker');
    const warnings = validationResults.filter(r => r.category === 'warning');
    
    return {
      canProceed: blockers.length === 0,
      blockerCount: blockers.length,
      warningCount: warnings.length,
      totalIssues: validationResults.length,
      blockers,
      warnings
    };
  };

  const validationSummary = getValidationSummary();

  return (
    <div className="min-h-screen bg-[#161513] flex">
      {/* Main Content */}
      <div className="flex-1">
        {/* Header */}
        <div className="bg-[#161513]/80 backdrop-blur-lg border-b border-[#0CB4E4]/20 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={onBack}
                className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Dashboard</span>
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          {/* Streamlined Scene Header - Dashboard Style */}
          <div className="mb-6 relative">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-white">{scene.name}</h1>
                <div className="flex flex-wrap items-center gap-3 mt-1 text-gray-300">
                  {/* Primary label: show capturePreset label if available; otherwise fallback to legacy scene.type */}
                  <span className="text-[#0CB4E4]">
                    {scene.capturePreset
                      ? scene.capturePreset.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
                      : scene.type
                        ? scene.type.charAt(0).toUpperCase() + scene.type.slice(1)
                        : ''}
                  </span>
                  {/* Camera type */}
                  {scene.cameraType && (
                    <>
                      <span className="mx-1.5">•</span>
                      {scene.cameraType.charAt(0).toUpperCase() + scene.cameraType.slice(1)}
                    </>
                  )}
                  {/* Capture setup – only show if captureType is defined */}
                  {scene.captureType && (
                    <>
                      <span className="mx-1.5">•</span>
                      {scene.captureType === 'single'
                        ? 'Single Camera'
                        : scene.captureType === 'dual'
                        ? 'Dual Camera'
                        : scene.captureType === 'triple'
                        ? 'Triple Camera'
                        : scene.captureType === 'quad'
                        ? 'Quad Camera'
                        : ''}
                    </>
                  )}
                  {scene.description && (
                    <span className="text-sm ml-1">- {scene.description}</span>
                  )}
                </div>
              </div>
              
              <div className="inline-flex items-center gap-3">
              <button
                onClick={() => window.location.href = '/dashboard'}
                className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white hover:bg-gray-600 rounded-lg text-xs font-medium transition-colors shadow-md"
                title="Delete this scene"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete scene
              </button>
            </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-[#0CB4E4]/10 border border-[#0CB4E4]/30 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[#0CB4E4] mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-[#0CB4E4]">Upload Error</h3>
                  <p className="text-[#0CB4E4] mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Image Uploader */}
          <div className="mb-8">
            <ImageUploader
              images={images}
              onImagesSelected={setImages}
              onImageClick={handleImageClick}
              captureType={scene.captureType}
              onValidationChange={handleValidationChange}
              onValidationStart={handleValidationStart}
            />
          </div>

          {/* ScanUpload Component */}
          {currentStep === 'upload-to-s3' && (
            <div className="mb-8">
              <ScanUpload
                ref={scanUploadRef}
                images={images}
                scene={scene}
                validationResults={validationResults}
                autoStart={false}
                hideButton={true}
                callbacks={{
                  onProgress: handleUploadProgress,
                  onComplete: handleUploadComplete,
                  onError: handleUploadError
                }}
              />
            </div>
          )}

          {/* Action Buttons */}
          {images.length > 0 && (
            <div className="bg-[#161513] rounded-xl shadow-lg border border-[#0CB4E4]/30 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="text-gray-300">
                    {images.length} pic{images.length === 1 ? '' : 's'} verified
                  </div>
                  
                  {/* Status Indicators */}
                  {currentStep === 'validating' && (
                    <div className="flex items-center gap-2 text-[#0CB4E4]">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm font-medium">Validating images...</span>
                    </div>
                  )}
                  
                  {scanCreationProgress.isCreating && (
                    <div className="flex items-center gap-2 text-[#0CB4E4]">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm font-medium">{scanCreationProgress.step}</span>
                    </div>
                  )}
                  
                  {currentStep === 'validation-complete' && !canReconstruct && (
                    <div className="flex items-center gap-2 text-[#0CB4E4]">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-sm font-medium">Fix validation issues</span>
                    </div>
                  )}
                  
                  {currentStep === 'upload-to-s3' && (
                    <div className="flex items-center gap-2 text-white">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Ready to upload</span>
                    </div>
                  )}
                  
                  {currentStep === 'ready-to-reconstruct' && (
                    <div className="flex items-center gap-2 text-[#0CB4E4]">
                      <Play className="w-4 h-4" />
                      <span className="text-sm font-medium">Ready for reconstruction</span>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-3">
                  {/* Upload New Dataset Button */}
                  <button
                    onClick={handleUploadNewDataset}
                    className="flex items-center gap-2 px-4 py-3 text-gray-300 hover:text-white bg-[#161513] hover:bg-[#161513]/80 border border-[#0CB4E4]/30 rounded-lg text-sm font-medium transition-colors shadow-sm h-12"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete all images
                  </button>
                  
                  {/* Upload to S3 Button */}
                  {currentStep === 'upload-to-s3' && (
                    <button
                      onClick={handleStartUpload}
                      className="flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all shadow-lg bg-gradient-to-r from-[#0CB4E4] to-[#0CB4E4]/80 text-white hover:from-[#0CB4E4]/90 hover:to-[#0CB4E4]/70 hover:shadow-xl transform hover:scale-[1.02] h-12 text-sm"
                    >
                      <Upload className="w-5 h-5" />
                      Upload
                    </button>
                  )}

                  {/* Reconstruct Button */}
                  {currentStep === 'ready-to-reconstruct' && (
                    <button
                      onClick={handleStartReconstruction}
                      disabled={scanCreationProgress.isCreating}
                      className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all shadow-lg ${
                        scanCreationProgress.isCreating
                          ? 'bg-gradient-to-r from-gray-700 to-gray-600 text-white cursor-not-allowed'
                          : 'bg-gradient-to-r from-[#0CB4E4] to-[#0CB4E4]/80 text-[#161513] hover:from-[#0CB4E4]/90 hover:to-[#0CB4E4]/70 hover:shadow-xl transform hover:scale-[1.02]'
                      }`}
                    >
                      {scanCreationProgress.isCreating ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Triggering 3D Reconstruction...
                        </>
                      ) : (
                        <>
                          <Play className="w-5 h-5" />
                          Start 3D Reconstruction
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Validation Sidebar */}
      {images.length > 0 && (
        <DetailedValidationSidebar 
          validationProgress={validationProgress}
          validationResults={validationResults}
          images={images}
        />
      )}
    </div>
  );
}